/* eslint-disable @typescript-eslint/no-explicit-any */
import clsx from 'clsx';
import Head from 'next/head';
import { useEffect, useRef, useState, useCallback } from 'react';

import { useSession } from '@marriott/mi-headless-utils';
import { Types, Button, Heading, tags, useDocumentDirection } from '@marriott/mi-ui-library';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

import { decodeHtml, getFallbackImg, isUserAuthenticated } from '../../utils/OfferUtils';
import { shouldUseMockData } from '../../utils/CommonUtils';
import config from '../../lib/config';
import { PANORAMIC_ASPECT_RATIO } from '../../constants/OffersHeroConstants';
import {
  IMAGE_CACHE_DOMAIN,
  IMAGE_CACHE_DOMAIN_CN,
  PANO_ASPECT_RATIO_UXL_FIELD_MAPPING,
} from '../../constants/CommonConstants';

import { OffersMBEPropertyProps } from './OffersHero.types';
import { StyledOffersHeroMBE } from './OffersHero.styles';

export const OffersMBE: React.FC<OffersMBEPropertyProps> = props => {
  const { isAuthorMode, modelObj, offersMBEProps } = props;
  const { datalayer } = useSession();
  const { envVarsObject } = useClientEnvVarsStore();
  const dir = useDocumentDirection();
  const CardLinkPrimaryBtnRef = useRef<HTMLButtonElement>(null);

  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean>(() => isUserAuthenticated(datalayer));
  const [mockPropsData, setMockPropsData] = useState<Record<string, unknown>>({});
  const [isMobile, setIsMobile] = useState(true);

  const OfferObj = !isAuthorMode
    ? offersMBEProps?.responseObject?.offers?.mbeOfferById?.offers
    : (mockPropsData?.['responseObject'] as any)?.offers?.mbeOfferById?.offers;

  const styleclass = modelObj?.styleclass;

  const handleScreenResize = useCallback(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  useEffect(() => {
    handleScreenResize();

    let timeoutId: ReturnType<typeof setTimeout>;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScreenResize, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(timeoutId);
    };
  }, [handleScreenResize]);

  // Lazy load mock data for AEM author page
  useEffect(() => {
    if (!isAuthorMode || !shouldUseMockData(isAuthorMode)) return;

    let isMounted = true;
    import('./__mock__/OffersHero.mock').then(mockData => {
      if (isMounted) {
        setMockPropsData(mockData?.mockOffersMBEProps?.offersMBEProps ?? {});
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isAuthorMode]);

  useEffect(() => {
    setIsUserLoggedIn(isUserAuthenticated(datalayer));
  }, [datalayer]);

  const imageDomain = envVarsObject?.['UXL_IMAGE_CACHE_DOMAIN'] ?? IMAGE_CACHE_DOMAIN;

  const isChinaDomain = imageDomain === IMAGE_CACHE_DOMAIN_CN;

  const fallBackImg = getFallbackImg(
    isChinaDomain ? config.FALLBACK_CN_IMG_URI : config.FALLBACK_IMG_URI,
    PANORAMIC_ASPECT_RATIO
  );
  const imageUrls = OfferObj?.media?.primaryImage?.imageUrls;
  const objKey = PANORAMIC_ASPECT_RATIO as keyof typeof PANO_ASPECT_RATIO_UXL_FIELD_MAPPING;
  const DACImagePath = imageUrls?.[PANO_ASPECT_RATIO_UXL_FIELD_MAPPING[objKey]];
  const desktopImageSrc = DACImagePath ? imageDomain + DACImagePath : imageDomain + fallBackImg;
  const imageAltText = OfferObj?.media?.primaryImage?.caption || OfferObj?.title;
  const imageTitle = OfferObj?.media?.primaryImage?.title;

  const header = decodeHtml(OfferObj?.title);
  const description = decodeHtml(OfferObj?.description);

  const headerTag = modelObj?.headerTag ?? tags.h2;

  const hasPrimaryButton = Boolean(modelObj?.primaryCtaText?.length && modelObj?.primaryCtaLink?.length);
  const hasSecondaryButton = Boolean(modelObj?.secondaryCtaText?.length && modelObj?.secondaryCtaLink?.length);

  const showPrimaryCta = hasPrimaryButton && !(modelObj?.primaryCtaHide && isUserLoggedIn);
  const showSecondaryCta = hasSecondaryButton && !(modelObj?.secondaryCtaHide && isUserLoggedIn);

  const hasStandard = styleclass?.includes('standard');
  const hasColorScheme = styleclass?.split(' ').some((cls: string) => cls.startsWith('color-scheme'));

  const buttonSize = isMobile ? 'm-button-s' : 'm-button-m';
  const ellipsisClass = isMobile ? 'm-ellipsis-3lines' : 'm-ellipsis-2lines';

  return (
    <>
      {desktopImageSrc && (
        <Head>
          <link rel="preconnect" href={imageDomain} crossOrigin="anonymous" />
          <link
            rel="preload"
            fetchPriority="high"
            as="image"
            href={desktopImageSrc}
            imageSrcSet={`${desktopImageSrc} 1920w`}
          />
        </Head>
      )}
      <StyledOffersHeroMBE
        data-component-name="m-offers-mbe-hero"
        data-testid="offers-mbe-hero"
        scrimGradient={styleclass}
        dir={dir}
        className={clsx('row', 'hero', 'm-container-fullbleed', {
          [styleclass]: hasStandard || hasColorScheme,
          inverse: !hasStandard && !hasColorScheme,
        })}
      >
        <div className="hero_item hero-img-wrapper">
          <div className="d-sm-block hero-img-container">
            <img
              src={desktopImageSrc}
              className={'feature-img'}
              alt={imageAltText ?? imageTitle}
              title={imageTitle ?? imageAltText}
              fetchPriority="high"
              width={1920}
              height={540}
              decoding="async"
              loading="eager"
            />
          </div>
          <div
            className={clsx('hero__cnt-sec', {
              gradient: header || description || modelObj?.primaryCtaText || modelObj?.secondaryCtaText,
            })}
          >
            <div className="container-sm hero__cnt-ctr">
              <div className="row">
                <div className="hero_content col-12 col-sm-6 col-lg-6">
                  <Heading
                    customClass={`hero_head ${ellipsisClass}`}
                    titleText={header}
                    variation={Types.headingType.title}
                    fontSize={Types.size.medium}
                    element={headerTag}
                  />
                  {description && (
                    <p
                      className="hero_desc t-font-m m-ellipsis-2lines"
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-md-8 col-lg-12">
                  <div className="hero_cta-wrapper hero_cta-center">
                    {showPrimaryCta && (
                      <Button
                        isLink
                        href={modelObj?.primaryCtaLink}
                        className={clsx('mb-2 hero_cta m-button-primary', buttonSize, {
                          'm-button-external': modelObj?.primaryCtaOpenInNewTab,
                        })}
                        target={modelObj?.primaryCtaOpenInNewTab ? '_blank' : undefined}
                        trackingProperties={modelObj?.trackingProperties}
                        ref={CardLinkPrimaryBtnRef}
                      >
                        {modelObj?.primaryCtaText}
                      </Button>
                    )}
                    {showSecondaryCta && (
                      <Button
                        isLink
                        href={modelObj?.secondaryCtaLink}
                        className={clsx('hero_cta m-button-secondary', buttonSize, {
                          hero_sec_cta: showPrimaryCta,
                          'm-button-external': modelObj?.secondaryCtaOpenInNewTab,
                        })}
                        target={modelObj?.secondaryCtaOpenInNewTab ? '_blank' : undefined}
                        custom_click_track_value={`${header}|${modelObj?.secondaryCtaText}|${
                          modelObj?.secondaryCtaOpenInNewTab ? 'external' : 'internal'
                        }`}
                      >
                        {modelObj?.secondaryCtaText}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </StyledOffersHeroMBE>
    </>
  );
};

export default OffersMBE;
